import React from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { CREATE_COMPANY_ADDRESS } from "../../graph/companyAddresses/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const validationSchema = yup.object().shape({
  companyID: yup.string().required("Company ID is required"),
  label: yup.string().nullable(),
  addressLine1: yup.string().trim().required("Address line 1 is required"),
  addressLine2: yup.string().nullable(),
  town: yup.string().nullable(),
  county: yup.string().nullable(),
  postcode: yup.string().nullable(),
  country: yup.string().trim().required("Country is required"),
  latitude: yup
    .number()
    .typeError("Latitude must be a number")
    .min(-90, "Latitude must be between -90 and 90")
    .max(90, "Latitude must be between -90 and 90")
    .nullable(),
  longitude: yup
    .number()
    .typeError("Longitude must be a number")
    .min(-180, "Longitude must be between -180 and 180")
    .max(180, "Longitude must be between -180 and 180")
    .nullable(),
  isPrimary: yup.boolean(),
});

const CreateCompanyAddressForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSnackbar } = useSnackbar();

  const passedCompanyID = location.state?.companyID || "";
  const redirectPath =
    location.state?.redirectPath ||
    (passedCompanyID ? `/companies/edit/${passedCompanyID}` : "/manageCompanies");

  const initialValues = {
    companyID: passedCompanyID,
    label: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    county: "",
    postcode: "",
    country: "United Kingdom",
    latitude: "",
    longitude: "",
    isPrimary: false,
  };

  const floatOrNull = (value) => {
    if (value === "" || value === null || value === undefined) return null;

    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const cleanedValues = { ...values };

      Object.keys(cleanedValues).forEach((key) => {
        if (cleanedValues[key] === "") cleanedValues[key] = null;
      });

      cleanedValues.latitude = floatOrNull(cleanedValues.latitude);
      cleanedValues.longitude = floatOrNull(cleanedValues.longitude);

      await client.request(CREATE_COMPANY_ADDRESS, {
        input: cleanedValues,
      });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Address created successfully",
      });

      resetForm();
      navigate(redirectPath, { state: { refresh: true } });
    } catch (err) {
      const message =
        err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("Error creating company address:", err);

      setSnackbar({
        open: true,
        severity: "error",
        message: `Failed to create address: ${message}`,
      });
    }
  };

  if (!passedCompanyID) {
    return (
      <Box>
        <Header
          title="CREATE ADDRESS"
          subtitle="A company must be selected before adding an address"
        />
        <Typography mt={3}>
          Open a company first, then add an address from the company edit page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Header
        title="CREATE ADDRESS"
        subtitle="Add an address linked to this company"
      />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <input
                type="hidden"
                name="companyID"
                value={values.companyID || ""}
              />

              <TextField
                label="Label"
                name="label"
                value={values.label}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.label && !!errors.label}
                helperText={touched.label && errors.label}
                sx={{ gridColumn: "span 2" }}
              />

              <Box sx={{ gridColumn: "span 2" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isPrimary}
                      onChange={(event) =>
                        setFieldValue("isPrimary", event.target.checked)
                      }
                    />
                  }
                  label="Primary address"
                />
              </Box>

              <TextField
                label="Address Line 1"
                name="addressLine1"
                value={values.addressLine1}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.addressLine1 && !!errors.addressLine1}
                helperText={touched.addressLine1 && errors.addressLine1}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Address Line 2"
                name="addressLine2"
                value={values.addressLine2}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.addressLine2 && !!errors.addressLine2}
                helperText={touched.addressLine2 && errors.addressLine2}
                sx={{ gridColumn: "span 4" }}
              />

              <TextField
                label="Town / City"
                name="town"
                value={values.town}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.town && !!errors.town}
                helperText={touched.town && errors.town}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="County"
                name="county"
                value={values.county}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.county && !!errors.county}
                helperText={touched.county && errors.county}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Postcode"
                name="postcode"
                value={values.postcode}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.postcode && !!errors.postcode}
                helperText={touched.postcode && errors.postcode}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Country"
                name="country"
                value={values.country}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.country && !!errors.country}
                helperText={touched.country && errors.country}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Latitude"
                name="latitude"
                type="number"
                value={values.latitude}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.latitude && !!errors.latitude}
                helperText={touched.latitude && errors.latitude}
                inputProps={{ step: "any" }}
                sx={{ gridColumn: "span 2" }}
              />

              <TextField
                label="Longitude"
                name="longitude"
                type="number"
                value={values.longitude}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.longitude && !!errors.longitude}
                helperText={touched.longitude && errors.longitude}
                inputProps={{ step: "any" }}
                sx={{ gridColumn: "span 2" }}
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" gap="10px" mt="20px">
              <ActionButton
                tone="ghost"
                onClick={() => navigate(redirectPath)}
              >
                Cancel
              </ActionButton>

              <ActionButton type="submit" tone="success">
                Create Address
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateCompanyAddressForm;